import { QueryKey } from '@crm/api'
import { client } from '@crm/api/client.gen'
import { InfiniteSelect } from '@crm/components'
import { listTransformKeyToString, stringifyUrl } from '@crm/utils'
import { Options, TDataShape } from '@hey-api/client-axios'
import { InfiniteData, UseInfiniteQueryOptions } from '@tanstack/react-query'
import { Form, Select } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { BaseOptionType } from 'antd/lib/select'
import { AxiosError } from 'axios'
import { useCallback, useMemo } from 'react'

import { TaskFormElement } from './types'

type Props = {
  element: TaskFormElement
  options?: BaseOptionType[]
  required?: boolean
}

export const SelectElement = ({ element, options, required }: Props) => {
  const api = element?.props?.api as string | undefined

  const getQueryFunction = useCallback(
    (
      queryOptions: Options<TDataShape>
    ): UseInfiniteQueryOptions<
      AnyObject,
      AxiosError<Error, unknown>,
      InfiniteData<AnyObject>,
      AnyObject,
      QueryKey<Options<TDataShape>>,
      | number
      | Pick<
          QueryKey<Options<TDataShape>>[0],
          'body' | 'headers' | 'path' | 'query'
        >
    > => {
      const queryKey: QueryKey<Options<TDataShape>> = [
        {
          query: queryOptions.query || {},
          path: queryOptions.path || {},
          body: queryOptions.body,
          headers: queryOptions.headers
        }
      ] as QueryKey<Options<TDataShape>>

      return {
        queryKey,
        queryFn: async (context) => {
          if (!api) {
            throw new Error('API endpoint is not defined')
          }

          const pageParam =
            typeof context.pageParam === 'number' ? context.pageParam : 1
          const queryParams = {
            ...(queryOptions.query as Record<string, unknown>),
            page: pageParam,
            per_page: 20
          }

          const response = await client.request({
            method: 'GET',
            url: stringifyUrl(`${api}`, queryParams)
          })

          const data = response.data as {
            results?: unknown[]
            next?: string | number | null
            count?: number
          }

          return {
            results: data?.results || (Array.isArray(data) ? data : []),
            next: data?.next
          }
        },
        getNextPageParam: (lastPage) => {
          const next = lastPage?.next

          return typeof next === 'number' ? next : undefined
        },
        initialPageParam: 1,
        enabled: !!api
      }
    },
    [api]
  )

  const staticOptions = useMemo(() => {
    return options?.map((item) => ({
      label: item.text,
      value: item.value
    }))
  }, [options])

  const renderSelect = () => {
    if (staticOptions) {
      return <Select placeholder={element.label} options={staticOptions} />
    }

    if (api) {
      return (
        <InfiniteSelect<TDataShape, AnyObject, Record<string, unknown>>
          getQueryFunction={getQueryFunction}
          placeholder={element.label}
          getOptions={({ options }) => listTransformKeyToString(options || [])}
        />
      )
    }

    return null
  }

  return (
    <Form.Item name={element.name} label={element.label} rules={[{ required }]}>
      {renderSelect()}
    </Form.Item>
  )
}
